import { t } from 'i18next';
import {
  NODE_CATEGORIES,
  VALID_APPROVAL_TARGETS,
  VALID_SEND_BACK_TARGETS,
} from '../../../../../constants/workflow.constant';
import {
  getNodeFormIds,
  findUpstreamNodeIds,
  getAssessmentParentReviewId,
  findDownstreamFormIds,
} from '../utils/graphHelpers';

export const validateApprovalNodes = (nodes, edges) => {
  const reviewNodes = nodes.filter(
    (node) => node.category === NODE_CATEGORIES.REVIEW
  );
  const validReviewTargets = [
    NODE_CATEGORIES.CHILD_APP,
    NODE_CATEGORIES.CHILD_REJ,
    NODE_CATEGORIES.CHILD_SBK,
    NODE_CATEGORIES.CHILD_STA,
  ];

  for (const reviewNode of reviewNodes) {
    const reviewName =
      reviewNode.data?.formConfig?.name ||
      reviewNode.data?.name ||
      reviewNode.id;
    const outgoingEdges = edges.filter((edge) => edge.source === reviewNode.id);

    for (const edge of outgoingEdges) {
      const targetNode = nodes.find((node) => node.id === edge.target);
      if (!targetNode || !validReviewTargets.includes(targetNode.category)) {
        return {
          message: t('workflowValidation.reviewMustConnectToAction', {
            name: reviewName,
          }),
        };
      }
    }
  }

  const allActionNodes = nodes.filter(
    (node) =>
      node.category === NODE_CATEGORIES.CHILD_APP ||
      node.category === NODE_CATEGORIES.CHILD_REJ ||
      node.category === NODE_CATEGORIES.CHILD_SBK
  );

  for (const actionNode of allActionNodes) {
    const incomingEdges = edges.filter((edge) => edge.target === actionNode.id);
    for (const edge of incomingEdges) {
      const sourceNode = nodes.find((node) => node.id === edge.source);
      if (
        !sourceNode ||
        (sourceNode.category !== NODE_CATEGORIES.REVIEW &&
          sourceNode.category !== NODE_CATEGORIES.ASSESSMENT_REVIEW)
      ) {
        return {
          message: t('workflowValidation.actionNodeMustConnectFromReview', {
            name: actionNode?.data?.name,
          }),
        };
      }
    }
  }

  const getPrimaryEdges = (allEdges, allNodes) =>
    allEdges.filter((e) => {
      const srcNode = allNodes.find((n) => n.id === e.source);
      return srcNode?.category !== NODE_CATEGORIES.CHILD_SBK;
    });

  const validateApproveOrRejectNodes = (actionNodes, errorKeys) => {
    for (const actionNode of actionNodes) {
      const outgoingEdges = edges.filter(
        (edge) => edge.source === actionNode.id
      );
      const parentEdge = edges.find((edge) => edge.target === actionNode.id);
      const parentReviewNode = parentEdge
        ? nodes.find((node) => node.id === parentEdge.source)
        : null;

      for (const edge of outgoingEdges) {
        const targetNode = nodes.find((node) => node.id === edge.target);

        if (parentReviewNode?.category === NODE_CATEGORIES.ASSESSMENT_REVIEW) {
          const grandparentReviewId = getAssessmentParentReviewId(
            parentReviewNode.id,
            edges,
            nodes
          );
          if (!grandparentReviewId || targetNode?.id !== grandparentReviewId) {
            return {
              message: t(
                'workflowValidation.approveRejectMustConnectToParentReview'
              ),
            };
          }
          continue;
        }

        if (
          !targetNode ||
          !VALID_APPROVAL_TARGETS.includes(targetNode.category)
        ) {
          return {
            message: t(errorKeys.connectionError, {
              name: actionNode?.data?.name,
            }),
          };
        }

        if (
          parentReviewNode &&
          (targetNode.category === NODE_CATEGORIES.REVIEW ||
            targetNode.category === NODE_CATEGORIES.SUBMISSION)
        ) {
          const primaryEdges = getPrimaryEdges(edges, nodes);
          const upstreamOfParent = findUpstreamNodeIds(
            parentReviewNode.id,
            primaryEdges
          );
          if (upstreamOfParent.has(targetNode.id)) {
            return {
              message: t(errorKeys.backwardError, {
                name: actionNode?.data?.name,
              }),
            };
          }

          if (targetNode.category === NODE_CATEGORIES.REVIEW) {
            const targetReviewFormIds = getNodeFormIds(targetNode);
            if (targetReviewFormIds.length > 0) {
              const upstreamSubmittedForms = new Set();
              for (const nodeId of upstreamOfParent) {
                const node = nodes.find((n) => n.id === nodeId);
                if (node?.category === NODE_CATEGORIES.SUBMISSION) {
                  getNodeFormIds(node).forEach((id) =>
                    upstreamSubmittedForms.add(id)
                  );
                }
              }
              for (const formId of targetReviewFormIds) {
                if (!upstreamSubmittedForms.has(formId)) {
                  const reviewName =
                    targetNode.data?.formConfig?.name || targetNode.id;
                  return {
                    message: t(
                      'workflowValidation.approveSkipsRequiredSubmission',
                      {
                        name: actionNode?.data?.name,
                        reviewName,
                      }
                    ),
                  };
                }
              }
            }
          }
        }
      }
    }
    return null;
  };

  const approveError = validateApproveOrRejectNodes(
    nodes.filter((node) => node.category === NODE_CATEGORIES.CHILD_APP),
    {
      connectionError: 'workflowValidation.approvalNodeConnectionError',
      backwardError: 'workflowValidation.approveCannotGoBackward',
    }
  );
  if (approveError) return approveError;

  const sendBackNodes = nodes.filter(
    (node) => node.category === NODE_CATEGORIES.CHILD_SBK
  );
  for (const sendBackNode of sendBackNodes) {
    const incomingEdges = edges.filter(
      (edge) => edge.target === sendBackNode.id
    );
    const outgoingEdges = edges.filter(
      (edge) => edge.source === sendBackNode.id
    );

    if (incomingEdges.length !== 1) {
      return {
        message: t('workflowValidation.sendBackSingleIncoming', {
          name: sendBackNode?.data?.name,
        }),
      };
    }

    if (outgoingEdges.length !== 1) {
      return {
        message: t('workflowValidation.actionNodeSingleConnection', {
          name: sendBackNode?.data?.name,
        }),
      };
    }

    const targetEdge = outgoingEdges[0];
    const targetNode = nodes.find((node) => node.id === targetEdge.target);
    if (!targetNode || !VALID_SEND_BACK_TARGETS.includes(targetNode.category)) {
      return {
        message: t('workflowValidation.sendBackNodeConnectionError', {
          name: sendBackNode?.data?.name,
        }),
      };
    }

    const parentEdge = incomingEdges[0];
    const parentReviewNode = nodes.find(
      (node) => node.id === parentEdge.source
    );

    if (parentEdge.source === targetNode.id) {
      return {
        message: t('workflowValidation.sendBackLoopError'),
      };
    }

    const upstreamOfParent = findUpstreamNodeIds(parentReviewNode.id, edges);
    if (!upstreamOfParent.has(targetNode.id)) {
      return {
        message: t('workflowValidation.sendBackMustGoBackward'),
      };
    }

    if (parentReviewNode?.category === NODE_CATEGORIES.REVIEW) {
      const parentReviewForms = new Set(getNodeFormIds(parentReviewNode));

      const downstreamForms = findDownstreamFormIds(
        targetNode.id,
        parentReviewNode.id,
        nodes,
        edges
      );

      for (const formId of downstreamForms) {
        if (!parentReviewForms.has(formId)) {
          return {
            message: t('workflowValidation.sendBackInvalidTarget'),
          };
        }
      }

      if (targetNode.category === NODE_CATEGORIES.REVIEW) {
        const parentRoleId =
          parentReviewNode.data?.formConfig?.approver_role_id;
        const targetRoleId = targetNode.data?.formConfig?.approver_role_id;

        if (parentRoleId && targetRoleId && parentRoleId === targetRoleId) {
          return {
            message: t('workflowValidation.sendBackSameReviewerRole', {
              name: sendBackNode?.data?.name,
            }),
          };
        }
      }
    }
  }

  const rejectError = validateApproveOrRejectNodes(
    nodes.filter((node) => node.category === NODE_CATEGORIES.CHILD_REJ),
    {
      connectionError: 'workflowValidation.rejectNodeConnectionError',
      backwardError: 'workflowValidation.rejectCannotGoBackward',
    }
  );
  if (rejectError) return rejectError;

  const endNodes = nodes.filter(
    (node) => node.category === NODE_CATEGORIES.END
  );
  for (const endNode of endNodes) {
    const incomingEdges = edges.filter((edge) => edge.target === endNode.id);
    const incomingNodes = incomingEdges
      .map((edge) => nodes.find((node) => node.id === edge.source))
      .filter(Boolean);

    const reviewNodesConnectedToEnd = incomingNodes.filter(
      (node) => node.category === NODE_CATEGORIES.REVIEW
    );
    if (reviewNodesConnectedToEnd.length > 0) {
      return {
        message: t('workflowValidation.reviewCannotConnectToEnd'),
      };
    }

    const childAppNodes = incomingNodes.filter(
      (node) => node.category === NODE_CATEGORIES.CHILD_APP
    );
    const childRejNodes = incomingNodes.filter(
      (node) => node.category === NODE_CATEGORIES.CHILD_REJ
    );

    if (childAppNodes.length === 0 && childRejNodes.length === 0) {
      return {
        message: t('workflowValidation.endNodeConnectionError', {
          name: endNode?.data?.name,
        }),
      };
    }
  }

  return null;
};
